// src/pages/Users/UsersView.tsx
import React from 'react'
import Sidebar from '../../components/Layout/Sidebar'
import Header from '../../components/Layout/Header'
import Table from '../../components/UI/Table'
import Card from '../../components/UI/Card'
import Button from '../../components/UI/Button'
import {
    Search, Eye, Phone, DollarSign,
    Smartphone, User as UserIcon, X, Activity,
    ShoppingBag, ArrowUpDown, UserPlus, CheckCircle, AlertCircle
} from 'lucide-react'
import {User, UserActivity, Transaction} from '../../types'
import {formatPhone, formatNumberWithCommas, formatDate} from '../../utils/helpers'
import {SortField} from './UsersModel'
import {useNavigate} from 'react-router-dom'

interface UsersViewProps {
    filteredAndSortedUsers: User[]
    loading: boolean
    search: string
    selectedUser: User | null
    userActivity: UserActivity | null
    activityLoading: boolean
    sortField: SortField
    sortOrder: 'asc' | 'desc'
    sidebarOpen: boolean
    showConfirmDialog: boolean
    userToToggle: User | null
    toastMessage: {type: 'success' | 'error', text: string} | null  // 👇 أضفنا هذا
    setSearch: (value: string) => void
    setShowConfirmDialog: (value: boolean) => void
    setUserToToggle: (user: User | null) => void
    setToastMessage: (message: {type: 'success' | 'error', text: string} | null) => void  // 👇 وأضفنا هذا
    handleViewActivity: (user: User) => Promise<void>
    handleSort: (field: SortField) => void
    handleToggleUserStatus: (user: User) => void
    confirmToggleUserStatus: () => Promise<void>
    closeModal: () => void
    toggleSidebar: () => void
}

// دالة لتحديد إذا كان المستخدم متصل
const isUserOnline = (lastActive?: string): boolean => {
    if (!lastActive) return false;
    const last = new Date(lastActive).getTime();
    const now = new Date().getTime();
    const fiveMinutes = 5 * 60 * 1000;
    return (now - last) < fiveMinutes;
};

export const UsersView: React.FC<UsersViewProps> = ({
    filteredAndSortedUsers,
    loading,
    search,
    selectedUser,
    userActivity,
    activityLoading,
    sortField,
    sortOrder,
    sidebarOpen,
    showConfirmDialog,
    userToToggle,
    toastMessage,           // 👇 استقبلناها
    setSearch,
    setShowConfirmDialog,
    setUserToToggle,
    setToastMessage,        // 👇 استقبلناها
    handleViewActivity,
    handleSort,
    handleToggleUserStatus,
    confirmToggleUserStatus,
    closeModal,
    toggleSidebar
}) => {
    const navigate = useNavigate()

    console.log('👀 UsersView rendered with:', filteredAndSortedUsers)
    if (filteredAndSortedUsers.length > 0) {
        console.log('👀 First user:', filteredAndSortedUsers[0])
    }

    const columns = [
        {
            key: 'phone_number',
            title: 'رقم الهاتف',
            render: (user: User) => (
                <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900 tel-style">
                        {formatPhone(user.phone_number)}
                    </span>
                </div>
            )
        },
        {
            key: 'balance_usd',
            title: 'الرصيد',
            render: (user: User) => (
                <div className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium text-gray-900">{formatNumberWithCommas(user.balance_usd)}</span>
                </div>
            )
        },
        {
            key: 'role',
            title: 'النوع',
            render: (user: User) => (
                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.role === 'admin'
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-gray-100 text-gray-800'
                    }`}>
                    {user.role === 'admin' ? 'مدير' : 'مستخدم'}
                </span>
            )
        },
        {
            key: 'device_type',
            title: 'الجهاز',
            render: (user: User) => (
                <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{user.device_type || 'غير معروف'}</span>
                </div>
            )
        },
        {
            key: 'status',
            title: 'الحالة',
            render: (user: User) => (
                <div className="flex flex-col gap-1">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full w-fit ${user.is_active === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {user.is_active === 1 ? 'نشط' : 'محظور'}
                    </span>
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full w-fit ${isUserOnline(user.last_active) ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                        {isUserOnline(user.last_active) ? 'متصل' : 'غير متصل'}
                    </span>
                </div>
            )
        },
        {
            key: 'actions',
            title: 'الإجراءات',
            render: (user: User) => (
                <div className="flex gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        icon={<Eye className="w-4 h-4" />}
                        onClick={(e) => {
                            e.stopPropagation()
                            handleViewActivity(user)
                        }}
                    >
                        عرض
                    </Button>

                    {/* هذا الزر الجديد - يظهر فقط للمستخدمين العاديين (ليس أدمن) */}
                    {user.role !== 'admin' && (
                        <Button
                            size="sm"
                            variant={user.is_active === 1 ? "danger" : "success"}
                            onClick={(e) => {
                                e.stopPropagation()
                                handleToggleUserStatus(user)
                            }}
                        >
                            {user.is_active === 1 ? 'حظر' : 'تفعيل'}
                        </Button>
                    )}
                </div>
            )
        },
    ]

    return (
        <div className="min-h-screen bg-gray-50">
            {/* 👇 Toast Message - هنا التعديل */}
            {toastMessage && (
                <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in-down">
                    <div className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px] justify-center ${toastMessage.type === 'success'
                        ? 'bg-green-500 text-white'
                        : 'bg-red-500 text-white'
                        }`}>
                        {toastMessage.type === 'success'
                            ? <CheckCircle className="w-5 h-5" />
                            : <AlertCircle className="w-5 h-5" />
                        }
                        <span className="font-medium">{toastMessage.text}</span>
                    </div>
                </div>
            )}

            <Header toggleSidebar={toggleSidebar} />
            <Sidebar isOpen={sidebarOpen} onClose={() => toggleSidebar()} isMobile={true} />
            <div className="hidden lg:block"><Sidebar isMobile={false} /></div>

            <main className="pt-16 lg:pr-64">
                <div className="p-4 lg:p-6">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800">المستخدمين</h1>
                            <p className="text-gray-500 mt-1">إدارة ومتابعة المستخدمين</p>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="relative w-full md:w-96">
                                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="بحث برقم الهاتف أو المعرف..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    dir="rtl"
                                />
                            </div>

                            <Button onClick={() => navigate('/users/add')} icon={<UserPlus className="w-4 h-4" />}>
                                إضافة مستخدم
                            </Button>
                        </div>
                    </div>

                    {/* Sort Controls */}
                    <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex flex-wrap gap-3">
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                            <ArrowUpDown className="w-4 h-4" />
                            ترتيب حسب:
                        </span>
                        <button onClick={() => handleSort('balance_usd')}
                            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${sortField === 'balance_usd' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                            الرصيد {sortField === 'balance_usd' && (sortOrder === 'desc' ? '↓' : '↑')}
                        </button>
                        <button onClick={() => handleSort('created_at')}
                            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${sortField === 'created_at' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                            تاريخ التسجيل {sortField === 'created_at' && (sortOrder === 'desc' ? '↓' : '↑')}
                        </button>
                        <button onClick={() => handleSort('phone_number')}
                            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${sortField === 'phone_number' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                            رقم الهاتف {sortField === 'phone_number' && (sortOrder === 'desc' ? '↓' : '↑')}
                        </button>
                    </div>

                    {/* Users Table */}
                    <Card>
                        <Table columns={columns} data={filteredAndSortedUsers} loading={loading} emptyMessage="لا يوجد مستخدمين" />
                    </Card>

                    {/* User Activity Modal */}
                    {selectedUser && (
                        <UserActivityModal
                            user={selectedUser}
                            activity={userActivity}
                            loading={activityLoading}
                            onClose={closeModal}
                        />
                    )}

                    {/* Confirm Dialog */}
                    {showConfirmDialog && userToToggle && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-gray-800 mb-2">تأكيد العملية</h3>
                                    <p className="text-gray-600">
                                        هل أنت متأكد من {userToToggle.is_active === 1 ? 'حظر' : 'تفعيل'} المستخدم
                                        <span className="font-bold text-blue-600 block mt-2 text-lg" dir="ltr">
                                            {userToToggle.phone_number}
                                        </span>
                                    </p>
                                </div>
                                <div className="border-t border-gray-200 p-4 flex justify-end gap-3">
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setShowConfirmDialog(false)
                                            setUserToToggle(null)
                                        }}
                                    >
                                        إلغاء
                                    </Button>
                                    <Button
                                        variant={userToToggle.is_active === 1 ? "danger" : "success"}
                                        onClick={confirmToggleUserStatus}
                                    >
                                        {userToToggle.is_active === 1 ? 'حظر' : 'تفعيل'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    )
}

// User Activity Modal Component
const UserActivityModal: React.FC<{
    user: User
    activity: UserActivity | null
    loading: boolean
    onClose: () => void
}> = ({user, activity, loading, onClose}) => {
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <Activity className="w-5 h-5 text-blue-500" />
                        نشاط المستخدم
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* User Info */}
                <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 border-b border-gray-200">
                    <div className="flex items-center gap-4">
                        <div className="bg-blue-500 p-3 rounded-full">
                            <UserIcon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-gray-500">رقم الهاتف</p>
                                    <p className="text-sm font-medium text-gray-900">{formatPhone(user.phone_number)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">الرصيد</p>
                                    <p className="text-sm font-medium text-green-600">{formatNumberWithCommas(user.balance_usd)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">المحفظة</p>
                                    <p className="text-sm font-medium text-blue-600">
                                        {user.id?.startsWith('A') ? 'Alfa' : user.id?.startsWith('T') ? 'Touch' : 'غير معروف'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">نوع الجهاز</p>
                                    <p className="text-sm text-gray-600">{user.device_type || 'غير معروف'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">البريد الإلكتروني</p>
                                    <p className="text-sm text-gray-600">{user.email || 'لا يوجد'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">تاريخ التسجيل</p>
                                    <p className="text-sm text-gray-600">{formatDate(user.created_at)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">الحالة</p>
                                    <p className={`text-sm font-medium ${user.is_active === 1 ? 'text-green-600' : 'text-red-600'}`}>
                                        {user.is_active === 1 ? 'نشط' : 'محظور'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">آخر نشاط</p>
                                    <p className="text-sm text-gray-600">{user.last_active ? formatDate(user.last_active) : 'غير معروف'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Activity Content */}
                <div className="p-6">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                    ) : activity ? (
                        <>
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <StatCard label="إجمالي المعاملات" value={activity.stats?.total_tx || 0} color="blue" />
                                <StatCard label="إجمالي المشتريات" value={formatNumberWithCommas(activity.stats?.buy_volume || 0)} color="green" />
                                <StatCard label="إجمالي المبيعات" value={formatNumberWithCommas(activity.stats?.sell_volume || 0)} color="orange" />
                                <StatCard label="متوسط المعاملة" value={formatNumberWithCommas(activity.stats?.avg_tx_amount || 0)} color="purple" />
                            </div>

                            <h3 className="font-semibold text-gray-800 mb-4">آخر النشاطات</h3>
                            <div className="space-y-3">
                                {activity.recent_transactions?.map((tx: Transaction) => (
                                    <TransactionItem key={tx.id} transaction={tx} />
                                ))}
                            </div>
                        </>
                    ) : (
                        <p className="text-center text-gray-500 py-8">لا توجد بيانات نشاط</p>
                    )}
                </div>
            </div>
        </div>
    )
}

const StatCard: React.FC<{label: string; value: string | number; color: 'blue' | 'green' | 'orange' | 'purple'}> = ({label, value, color}) => {
    const colors = {
        blue: 'bg-blue-50 text-blue-600',
        green: 'bg-green-50 text-green-600',
        orange: 'bg-orange-50 text-orange-600',
        purple: 'bg-purple-50 text-purple-600'
    }

    return (
        <div className={`${colors[color]} rounded-lg p-4 text-center`}>
            <p className="text-sm text-gray-600 mb-1">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
        </div>
    )
}

const TransactionItem: React.FC<{transaction: Transaction}> = ({transaction: tx}) => {
    return (
        <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
                <ShoppingBag className={`w-5 h-5 ${tx.type === 'BUY' ? 'text-green-500' : 'text-orange-500'}`} />
                <div>
                    <span className={`text-sm font-medium px-2 py-1 rounded ${tx.type === 'BUY' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                        {tx.type === 'BUY' ? 'شراء' : 'بيع'}
                    </span>
                    <span className="text-sm font-medium text-gray-900 mr-3">
                        {formatNumberWithCommas(tx.amount_usd)}
                    </span>
                </div>
            </div>
            <span className="text-xs text-gray-500">{formatDate(tx.created_at)}</span>
        </div>
    )
}